/** Catalogue membership uses Product.productUniqueId, never Mongo _id or a display label. */
export const catalogueMembershipId = (product: { productUniqueId?: unknown }): string =>
  typeof product?.productUniqueId === "string" ? product.productUniqueId.trim() : "";

export type RemovalResult = { affectedIds: string[]; notFoundIds: string[]; remainingCount: number };

export const confirmedCatalogueRemoval = (response: any, requestedIds: string[]): RemovalResult | null => {
  const data = response?.data;
  if (response?.success !== true || !Array.isArray(data?.affectedIds) ||
      !Array.isArray(data?.notFoundIds) || !Number.isInteger(data?.remainingCount) || data.remainingCount < 0 ||
      !Array.isArray(data?.requestedIds)) return null;
  const requested = new Set(requestedIds);
  const affectedIds = data.affectedIds;
  const notFoundIds = data.notFoundIds;
  if (requested.size !== requestedIds.length || requested.size !== data.requestedIds.length ||
      !data.requestedIds.every((id: unknown) => typeof id === "string" && requested.has(id)) ||
      affectedIds.length === 0 || affectedIds.length + notFoundIds.length !== requested.size ||
      ![...affectedIds, ...notFoundIds].every((id) => typeof id === "string" && requested.has(id)) ||
      new Set([...affectedIds, ...notFoundIds]).size !== requested.size) return null;
  return { affectedIds, notFoundIds, remainingCount: data.remainingCount };
};

const errorCopy: Record<string, string> = {
  PRODUCT_NOT_IN_CATALOGUE: "This product is no longer in the catalogue. Refresh the catalogue and try again.",
  CATALOGUE_NOT_FOUND: "This catalogue could not be found. Return to the catalogue list and try again.",
  PERMISSION_DENIED: "You don't have permission to change this catalogue. Ask a studio administrator for access.",
  VALIDATION_FAILED: "We couldn't remove the selected product. Refresh the catalogue and try again.",
  CONFLICT: "This catalogue changed while you were working. Refresh it and try again.",
  SYSTEM_MANAGED: "This catalogue is managed automatically. Products cannot be removed here.",
  SESSION_EXPIRED: "Your session has expired. Sign in again to continue.",
  NETWORK_UNAVAILABLE: "We couldn't confirm whether the catalogue changed. Check your connection, then refresh before trying again.",
};

export const catalogueRemovalError = (response: any): string =>
  errorCopy[response?.code] ||
  "We couldn't confirm that the product was removed. Refresh the catalogue before trying again.";

export const catalogueRemovalSuccess = (result: RemovalResult): string =>
  result.notFoundIds.length
    ? `${result.affectedIds.length} product${result.affectedIds.length === 1 ? "" : "s"} removed. ${result.notFoundIds.length} selected product${result.notFoundIds.length === 1 ? " was" : "s were"} no longer in this catalogue.`
    : `${result.affectedIds.length} product${result.affectedIds.length === 1 ? "" : "s"} removed from the catalogue.`;
